import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, formatDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import ExportPreview from "@/components/ExportPreview";
import PayrollCompare from "@/components/PayrollCompare";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calculator, Lock, Unlock, ClipboardCheck, Plus, SlidersHorizontal, AlertTriangle, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";

const ADJ_FIELDS = [
  ["hari_masuk", "Hari Masuk"], ["jam_lembur", "Jam Lembur"], ["kasbon", "Kasbon"],
  ["potongan_lain", "Potongan Lain"], ["tunjangan_lain", "Tunjangan Lain"],
];
const ADJ_KATEGORI = ["Bonus", "Koreksi Hari", "Koreksi Lembur", "Potongan Khusus", "Denda", "THR", "Kasbon", "Lainnya"];

export default function PayrollProcess() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [periods, setPeriods] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sel, setSel] = useState(null);
  const [details, setDetails] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ nama: "", tanggal_mulai: "", tanggal_selesai: "", project_id: "" });
  const [adj, setAdj] = useState(null);
  const [adjForm, setAdjForm] = useState({ field: "hari_masuk", value: 0, alasan: "", kategori: "Koreksi Hari" });
  const [confirmFinal, setConfirmFinal] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  const [recomputingAll, setRecomputingAll] = useState(false);
  const [calcPdf, setCalcPdf] = useState(null);

  const recomputeAll = async () => {
    setRecomputingAll(true);
    try {
      const r = await api.post("/payroll/recompute-all");
      toast.success(`${r.data.recomputed} periode (Draft/Diperiksa) dihitung ulang`);
      await loadPeriods();
      if (sel) loadDetails(sel.id);
    } catch (e) { toast.error(apiErr(e)); }
    setRecomputingAll(false);
  };

  const loadPeriods = () => api.get("/payroll/periods").then((r) => setPeriods(r.data));
  useEffect(() => { loadPeriods(); api.get("/projects").then((r) => setProjects(r.data)); }, []);
  const loadDetails = (pid) => api.get(`/payroll/periods/${pid}/details`).then((r) => setDetails(r.data));

  const pickPeriod = async (pid) => {
    const p = periods.find((x) => x.id === pid);
    setSel(p); loadDetails(pid);
  };

  const createPeriod = async () => {
    if (!form.nama || !form.tanggal_mulai || !form.tanggal_selesai) return toast.error("Lengkapi data periode");
    try {
      const r = await api.post("/payroll/periods", { ...form, project_id: form.project_id === "all" ? "" : form.project_id });
      toast.success("Periode dibuat"); setOpenNew(false); await loadPeriods(); setSel(r.data); setDetails([]);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const compute = async () => {
    try { const r = await api.post(`/payroll/periods/${sel.id}/compute`); toast.success(`Payroll dihitung: ${r.data.count} pegawai`); setSel({ ...sel, perlu_hitung_ulang: false }); loadPeriods(); loadDetails(sel.id); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const changeStatus = async (status) => {
    try { await api.post(`/payroll/periods/${sel.id}/status`, { status }); toast.success(`Status: ${status}`); await loadPeriods(); setSel({ ...sel, status }); loadDetails(sel.id); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doAdjust = async () => {
    try {
      await api.put(`/payroll/details/${adj.id}/adjust`, { field: adjForm.field, value: Number(adjForm.value), alasan: adjForm.alasan, kategori: adjForm.kategori });
      toast.success("Adjustment tersimpan"); setAdj(null); loadDetails(sel.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doUnlock = async () => {
    try {
      await api.post(`/payroll/periods/${sel.id}/unlock`, { alasan: unlockReason });
      toast.success("Payroll di-unlock, sekarang bisa dihitung ulang");
      setUnlockOpen(false); setUnlockReason("");
      await loadPeriods(); setSel({ ...sel, status: "DIPERIKSA" }); loadDetails(sel.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const locked = sel && ["FINAL", "DIBAYAR"].includes(sel.status);
  const totalBersih = details.reduce((s, d) => s + Number(d.gaji_bersih || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Proses Penggajian" subtitle="Hitung, adjust, periksa, dan finalisasi payroll">
        <Button variant="outline" onClick={recomputeAll} disabled={recomputingAll} data-testid="payroll-recompute-all-btn" title="Hitung ulang semua periode Draft/Diperiksa sekaligus"><RefreshCw className={`h-4 w-4 mr-1 ${recomputingAll ? "animate-spin" : ""}`} /> {recomputingAll ? "Menghitung…" : "Hitung Ulang Semua Draft"}</Button>
        <Button className="bg-blue-800 hover:bg-blue-900" data-testid="payroll-new-btn" onClick={() => { setForm({ nama: "", tanggal_mulai: "", tanggal_selesai: "", project_id: "all" }); setOpenNew(true); }}><Plus className="h-4 w-4 mr-1" /> Periode Baru</Button>
      </PageHeader>

      <Card className="p-4">
        <F label="Pilih Periode Payroll">
          <Select value={sel?.id || ""} onValueChange={pickPeriod}>
            <SelectTrigger className="max-w-md" data-testid="payroll-period-select"><SelectValue placeholder="Pilih periode" /></SelectTrigger>
            <SelectContent>{periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama} — {p.status}</SelectItem>)}</SelectContent>
          </Select>
        </F>
      </Card>

      {sel && (
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800">{sel.nama}</span>
                <StatusBadge status={sel.status} testid="payroll-status" />
              </div>
              <div className="text-sm text-slate-500">{formatDate(sel.tanggal_mulai)} — {formatDate(sel.tanggal_selesai)} · {sel.project_id ? (projects.find((p) => p.id === sel.project_id)?.nama || "Proyek") : "Semua Proyek"}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {details.length > 0 && <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => setCalcPdf({ url: `/payroll/periods/${sel.id}/calc.pdf`, title: `Rincian Perhitungan Gaji — ${sel.nama}`, file: `perhitungan_gaji_${sel.nama}.pdf` })} data-testid="payroll-calc-pdf-btn"><FileText className="h-4 w-4 mr-1" /> Preview Perhitungan PDF</Button>}
              {!locked && <Button variant="outline" onClick={compute} data-testid="payroll-compute-btn"><Calculator className="h-4 w-4 mr-1" /> Hitung Ulang Payroll</Button>}
              {sel.status === "DRAFT" && details.length > 0 && <Button variant="outline" onClick={() => changeStatus("DIPERIKSA")} data-testid="payroll-review-btn"><ClipboardCheck className="h-4 w-4 mr-1" /> Tandai Diperiksa</Button>}
              {sel.status === "DIPERIKSA" && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setConfirmFinal(true)} data-testid="payroll-finalize-btn"><Lock className="h-4 w-4 mr-1" /> Finalisasi & Lock</Button>}
              {locked && <span className="text-sm text-slate-500 flex items-center gap-1"><Lock className="h-4 w-4" /> Terkunci</span>}
              {locked && isSuper && <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => setUnlockOpen(true)} data-testid="payroll-unlock-btn"><Unlock className="h-4 w-4 mr-1" /> Unlock untuk Edit</Button>}
            </div>
          </div>
        </Card>
      )}

      {sel && sel.perlu_hitung_ulang && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="payroll-recompute-warning">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span className="flex-1"><b>Absensi atau Master Gaji berubah setelah gaji dihitung</b> (misalnya data dobel dirapikan, absensi dipindah proyek, atau tarif gaji baru diisi). {locked ? "Unlock periode dulu, lalu hitung ulang." : "Hitung ulang agar gaji & slip sesuai."}</span>
          {!locked && <Button className="bg-amber-600 hover:bg-amber-700" onClick={compute} data-testid="payroll-recompute-now-btn"><Calculator className="h-4 w-4 mr-1" /> Hitung Ulang Sekarang</Button>}
        </div>
      )}

      {sel && details.some((d) => !Number(d.gaji_per_hari)) && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="payroll-zero-rate-warning">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span className="flex-1"><b>Gaji/hari masih Rp 0</b> untuk: {details.filter((d) => !Number(d.gaji_per_hari)).map((d) => d.employee_nama).join(", ")}. Isi Master Gaji karyawan tersebut, lalu klik Hitung Ulang.</span>
        </div>
      )}

      {sel && details.length > 0 && <PayrollCompare periodId={sel.id} version={details.length + (sel.computed_at || "")} />}

      {sel && details.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">Preview Perhitungan</h2>
            <div className="text-sm text-slate-500">Total Gaji Bersih: <span className="font-mono font-bold text-slate-900">{rupiah(totalBersih)}</span></div>
          </div>
          {!locked && <p className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">Setiap kali mengubah tunjangan, absensi, atau tarif — klik <b>Hitung Ulang Payroll</b> agar perhitungan & slip gaji ikut diperbarui.</p>}
          <div className="overflow-x-auto scroll-thin rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="payroll-detail-table">
              <thead><tr className="border-b bg-slate-100/80 text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                {["Nama", "Hari", "Lembur", "Gaji", "Makan", "Luar Kota", "Uang Lembur", "Tunjangan", "Kasbon", "Potongan", "Gaji Bersih", ""].map((h, i) => (
                  <th key={i} className={`px-3 py-2.5 ${i >= 1 && i <= 10 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {details.map((d, i) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`payroll-row-${i}`}>
                    <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{d.employee_nama}<div className="text-xs text-slate-400">{d.project_nama}</div>
                      {(d.rincian_proyek || []).length > 1 && (
                        <div className="mt-1 space-y-0.5" data-testid={`payroll-rincian-${i}`}>
                          {d.rincian_proyek.map((x) => (
                            <div key={x.project_id} className="text-[11px] text-slate-600"><span className="rounded bg-orange-100 px-1 font-semibold text-orange-800">{x.project_nama}</span> {x.tanggal} · {x.hari} hr{x.lembur ? ` · ${x.lembur} j lembur` : ""}</div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{d.hari_masuk}</td>
                    <td className="px-3 py-2 text-right font-mono">{d.jam_lembur}</td>
                    <td className="px-3 py-2 text-right font-mono">{rupiah(d.gaji_pokok)}</td>
                    <td className="px-3 py-2 text-right font-mono">{rupiah(d.uang_makan)}</td>
                    <td className="px-3 py-2 text-right font-mono">{rupiah(d.uang_luar_kota)}</td>
                    <td className="px-3 py-2 text-right font-mono">{rupiah(d.uang_lembur)}</td>
                    <td className="px-3 py-2 text-right font-mono">{rupiah(d.total_tunjangan)}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-600">
                      {d.kasbon_capped && <AlertTriangle className="inline h-3.5 w-3.5 mr-1 text-amber-500" title="Sisa kasbon melebihi gaji periode" />}
                      {locked ? rupiah(d.kasbon) : (
                        <button className="rounded px-1.5 py-0.5 hover:bg-rose-50 underline decoration-dotted underline-offset-2" data-testid={`payroll-kasbon-edit-${i}`} onClick={() => { setAdj(d); setAdjForm({ field: "kasbon", value: d.kasbon, alasan: "" }); }}>{rupiah(d.kasbon)}</button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-rose-600">{rupiah(d.potongan_lain)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">{rupiah(d.gaji_bersih)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="icon" variant="ghost" className="text-orange-600" title="Preview perhitungan PDF" data-testid={`payroll-calc-pdf-${i}`} onClick={() => setCalcPdf({ url: `/payroll/periods/${sel.id}/calc.pdf?employee_id=${d.employee_id}`, title: `Perhitungan Gaji — ${d.employee_nama}`, file: `perhitungan_${d.employee_nama}.pdf` })}><FileText className="h-4 w-4" /></Button>
                      {!locked && <Button size="icon" variant="ghost" data-testid={`payroll-adjust-${i}`} onClick={() => { setAdj(d); setAdjForm({ field: "hari_masuk", value: d.hari_masuk, alasan: "" }); }}><SlidersHorizontal className="h-4 w-4" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sel && details.length === 0 && <div className="text-center text-slate-400 py-10">Belum ada perhitungan. Klik "Hitung Payroll" untuk menghitung dari data absensi.</div>}

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Buat Periode Payroll</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Nama Periode *"><Input placeholder="mis. Minggu 4 September 2026" data-testid="period-f-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Tanggal Mulai *"><Input type="date" data-testid="period-f-mulai" value={form.tanggal_mulai} onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })} /></F>
              <F label="Tanggal Selesai *"><Input type="date" data-testid="period-f-selesai" value={form.tanggal_selesai} onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })} /></F>
            </div>
            <F label="Proyek">
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="period-f-project"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua Proyek</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={createPeriod} data-testid="period-create-btn">Buat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adj} onOpenChange={() => setAdj(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Adjustment Manual — {adj?.employee_nama}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Komponen">
              <Select value={adjForm.field} onValueChange={(v) => setAdjForm({ ...adjForm, field: v, value: adj[v] })}>
                <SelectTrigger data-testid="adjust-field"><SelectValue /></SelectTrigger>
                <SelectContent>{ADJ_FIELDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Kategori Penyesuaian *">
              <Select value={adjForm.kategori} onValueChange={(v) => setAdjForm({ ...adjForm, kategori: v })}>
                <SelectTrigger data-testid="adjust-kategori"><SelectValue /></SelectTrigger>
                <SelectContent>{ADJ_KATEGORI.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Nilai Baru"><Input type="number" data-testid="adjust-value" value={adjForm.value} onChange={(e) => setAdjForm({ ...adjForm, value: e.target.value })} /></F>
            <F label="Alasan Perubahan * (muncul di slip gaji)"><Textarea data-testid="adjust-reason" value={adjForm.alasan} onChange={(e) => setAdjForm({ ...adjForm, alasan: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdj(null)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={doAdjust} data-testid="adjust-save-btn" disabled={!adjForm.alasan}>Simpan Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportPreview open={!!calcPdf} onClose={() => setCalcPdf(null)} title={calcPdf?.title} pdfUrl={calcPdf?.url}
        items={calcPdf ? [{ label: "Unduh PDF", url: calcPdf.url, filename: calcPdf.file }] : []} />

      <ConfirmDialog open={confirmFinal} onOpenChange={setConfirmFinal} testid="payroll-finalize-dialog"
        title="Finalisasi & Lock Payroll?" confirmText="Ya, Finalisasi"
        description="Setelah dikunci, absensi & tarif tidak akan mengubah payroll ini. Kasbon akan otomatis dipotong. Lanjutkan?"
        onConfirm={() => { setConfirmFinal(false); changeStatus("FINAL"); }} />

      <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Unlock Payroll — {sel?.nama}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Unlock mengembalikan status ke DIPERIKSA agar bisa dihitung ulang. Tercatat di Audit Log.</p>
          <F label="Alasan Unlock *"><Textarea data-testid="payroll-unlock-reason" value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)} /></F>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockOpen(false)}>Batal</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={doUnlock} data-testid="payroll-unlock-confirm" disabled={!unlockReason}>Unlock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
